import React from "react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { Container } from "../../components/container";
import { SkeletonGrid } from "../../components/skeleton";
import Auth from "../../functions/authenticate";
import Utility from "../../../jsx/utility/utility";
import SearchService from "../../search/service";
import { addRecent } from "../../search/recent";

/*
 * Full search results (/Search/:term, /Search/:section/:term) — the "See all
 * results" target of the toolbar search. Runs the SAME classified, gated
 * sources as the dropdown (search/service.js) with a larger per-source limit
 * and renders them grouped, with the same detail links. :section, when it
 * names a source (e.g. /Search/customers/acme), narrows the page to it.
 */
const PAGE_LIMIT = 25;
const css = {
  group: {
    marginBottom: 24
  },
  head: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 12,
    borderBottom: "1px solid var(--plk-border)",
    paddingBottom: 6,
    marginBottom: 4
  },
  label: {
    fontWeight: 600,
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: ".04em",
    color: "var(--plk-text-muted)"
  },
  row: {
    display: "block",
    padding: "8px 0",
    borderBottom: "1px solid var(--plk-border-subtle)",
    textDecoration: "none"
  },
  name: {
    color: "var(--plk-text)"
  },
  sub: {
    fontSize: 12,
    color: "var(--plk-text-muted)",
    marginTop: 2
  },
  empty: {
    padding: "16px 0",
    color: "var(--plk-text-muted)"
  }
};
export class SearchComponent extends React.Component {
  constructor(props) {
    super(props);
    this.service = new SearchService();
    this.state = {
      loading: true,
      ranked: [],
      classification: null
    };
    this._alive = true;
    this._seq = 0;
  }
  term() {
    return String(this.props.params && this.props.params.term || "");
  }
  section() {
    return this.props.params && this.props.params.section || "";
  }
  async componentDidMount() {
    Utility.fireEvent('app-layout-close-drawer', {});
    if (Auth.isAuthenticated() || (await Auth.attachOrSignIn())) this.load();
  }
  componentDidUpdate(prev) {
    const p = prev.params || {};
    const c = this.props.params || {};
    if (p.term !== c.term || p.section !== c.section) this.load();
  }
  componentWillUnmount() {
    this._alive = false;
    this.service.cancel();
  }
  load() {
    const term = this.term();
    const seq = ++this._seq;
    this.setState({
      loading: true,
      ranked: [],
      classification: null
    });
    if (term.trim()) addRecent(term);
    this.service.search(term, {
      debounce: 0,
      limit: PAGE_LIMIT,
      currentPath: this.props.location.pathname
    }).then(result => {
      if (!this._alive || seq !== this._seq || result.aborted) return;
      const section = this.section();
      const ranked = (result.ranked || []).filter(r => !section || r.entityKey === section);
      this.setState({
        loading: false,
        ranked,
        classification: result.classification
      });
    });
  }

  // ranked is already in display order (grouped by source) — split it at each source change.
  groups() {
    const out = [];
    this.state.ranked.forEach(r => {
      const last = out[out.length - 1];
      if (last && last.key === r.entityKey) last.items.push(r);else out.push({
        key: r.entityKey,
        label: r.label,
        config: r.config,
        items: [r]
      });
    });
    return out;
  }
  render() {
    const term = this.term();
    const {
      loading,
      classification
    } = this.state;
    const ctx = classification && classification.context || {};
    const groups = this.groups();
    return /*#__PURE__*/React.createElement(Container, {
      title: `Search results for "${term}"`
    }, loading && /*#__PURE__*/React.createElement(SkeletonGrid, {
      rows: 6,
      cols: 1
    }), !loading && !groups.length && /*#__PURE__*/React.createElement("div", {
      style: css.empty
    }, term.trim().length < 2 ? "Type at least two characters to search." : "Nothing matched that search."), !loading && groups.map(g => /*#__PURE__*/React.createElement("div", {
      key: g.key,
      style: css.group
    }, /*#__PURE__*/React.createElement("div", {
      style: css.head
    }, /*#__PURE__*/React.createElement("span", {
      style: css.label
    }, g.label, " \xB7 ", g.items.length, g.items.length >= PAGE_LIMIT ? "+" : ""), g.config.listLink && /*#__PURE__*/React.createElement(Link, {
      to: g.config.listLink(term, ctx)
    }, "Open list")), g.items.map((r, i) => {
      const name = g.config.displayName(r.row) || "";
      const sub = g.config.displaySub(r.row) || "";
      return /*#__PURE__*/React.createElement(Link, {
        key: `${g.key}-${i}`,
        to: g.config.detailLink(r.row, ctx),
        style: css.row
      }, /*#__PURE__*/React.createElement("div", {
        style: css.name
      }, name), sub && /*#__PURE__*/React.createElement("div", {
        style: css.sub
      }, sub));
    }))));
  }
}
const RoutableSearchComponent = withRouter(SearchComponent);

// Route entry (index.jsx: /Search/:term and /Search/:section/:term).
export class SearchViewComponent extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement(RoutableSearchComponent, {
      params: this.props.params
    });
  }
}
export default {
  SearchViewComponent
};